// The widget React root.
//
// Modes (top level):
//   - booking — the engaged booking flow (lead capture → strip + revealed panel)
//   - confirm-appointment — intermediate review screen before the SF write
//   - congrats — post-write confirmation + Get Set Up upsell form
//   - set-up — final "You're Set Up" acknowledgment
//
// Container-width responsive layout (ResizeObserver-driven, NOT viewport).
// Below 900px container width, the CSS collapses the strip + panel grids
// to single column. State machine + behavior are identical.

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import styles from './styles/widget.module.css';
import { ENGAGE_EVENT, emit } from './state/events';
import { reducer, initialState } from './state/machine';
import { bookingEditFromConfirmActions, type BookingEditField } from './utils/bookingFlow';
import { useContainerWidth } from './utils/useContainerWidth';
import { parsePrice } from './utils/pricing';
import { createBooking, createWaitlist, ApiError } from './api/client';
import { emitAnalytics, setAnalyticsUserId } from './analytics/dispatch';
import type { BookingPayload } from './types';

import { LeadCapture } from './steps/LeadCapture';
import { BookingStripPanel } from './steps/BookingStripPanel';
import { ConfirmAppointment } from './steps/ConfirmAppointment';
import { BookingConfirmed } from './steps/BookingConfirmed';
import { GetSetUp } from './steps/GetSetUp';
import { PostBookingComplete } from './steps/PostBookingComplete';
import { TrafficDrawer } from './components/TrafficDrawer';
import { Modal } from './components/Modal';

interface WidgetProps {
  embedSource: string;
}

type WidgetMode =
  | 'booking'
  | 'confirm-appointment'
  | 'congrats'
  | 'set-up'

const DESKTOP_BREAKPOINT = 1024;

export function Widget({ embedSource }: WidgetProps) {
  const [mode, setMode] = useState<WidgetMode | null>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [leadCaptureDone, setLeadCaptureDone] = useState(false);
  const [holdExpired, setHoldExpired] = useState(false);

  // Track the rendered root element in state so useContainerWidth can react
  // to it mounting (the div lives inside the modal portal and doesn't exist
  // until the modal opens). The ref is preserved for synchronous reads in
  // scroll handlers.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const setRoot = useCallback((el: HTMLDivElement | null) => {
    rootRef.current = el;
    setRootEl(el);
  }, []);
  const containerWidth = useContainerWidth(rootEl);
  const isDesktop = containerWidth >= DESKTOP_BREAKPOINT;

  // Engagement listener — host-page BOOK NOW dispatches `cc:booking-widget:engage`
  useEffect(() => {
    const onEngage = () => {
      setMode('booking');
      emit('cc:booking-widget:engaged', { source: embedSource });
      emitAnalytics({
        event: 'book_flow_start',
        embed_source: embedSource,
        entry_source: window.location.pathname || 'unknown',
      });
      // Scroll the widget mount point into view (offset for sticky-ish host header).
      const mount = document.querySelector('[data-cc-booking-widget]');
      if (mount) {
        const top = (mount as HTMLElement).getBoundingClientRect().top + window.scrollY - 24;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };
    window.addEventListener(ENGAGE_EVENT, onEngage);
    return () => window.removeEventListener(ENGAGE_EVENT, onEngage);
  }, [mode, embedSource]);

  // State-change watchers — fire analytics events when the user advances values.
  // Refs track previous values so we only emit on transitions, not on every render.
  const prevLocationId = useRef<string | null>(null);
  const prevDate = useRef<string | null>(null);
  const prevSlotKey = useRef<string | null>(null);
  const prevPromoOptIn = useRef<boolean>(false);
  const confirmScreenFired = useRef<boolean>(false);
  const confirmEnteredFromLocation = useRef<string | null>(null);
  const confirmEnteredFromFittingId = useRef<string | null>(null);

  // Location selected — fires book_flow_loc_selected. If a previous
  // location was set and the new one is different, also fires loc_change.
  useEffect(() => {
    if (state.selectedLocation && state.selectedLocation.id !== prevLocationId.current) {
      const previousId = prevLocationId.current;
      prevLocationId.current = state.selectedLocation.id;
      emitAnalytics({
        event: 'book_flow_loc_selected',
        location: state.selectedLocation.name,
        location_id: state.selectedLocation.id,
      });
      if (previousId !== null) {
        emitAnalytics({
          event: 'book_flow_loc_change',
          location_from: previousId,
          location_to: state.selectedLocation.id,
        });
      }
    }
  }, [state.selectedLocation]);

  // Date selected — fires book_flow_date_selected.
  useEffect(() => {
    if (state.selectedDate && state.selectedDate !== prevDate.current) {
      prevDate.current = state.selectedDate;
      emitAnalytics({
        event: 'book_flow_date_selected',
        date_selected: state.selectedDate,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    }
  }, [state.selectedDate]);

  // Time slot selected — fires book_flow_time_selected. Carries cumulative
  // location + fitting_selection so sGTM can reconstruct legacy `booking step=3`
  // with the full parameter shape the existing funnel expects.
  useEffect(() => {
    if (state.selectedSlot && state.selectedLocation && state.selectedFittingType) {
      const key = `${state.selectedSlot.start}|${state.selectedSlot.fitterId}`;
      if (key !== prevSlotKey.current) {
        prevSlotKey.current = key;
        const startDate = new Date(state.selectedSlot.start);
        const hh = String(startDate.getHours()).padStart(2, '0');
        const mm = String(startDate.getMinutes()).padStart(2, '0');
        emitAnalytics({
          event: 'book_flow_time_selected',
          time_selected: `${hh}:${mm}`,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          location: state.selectedLocation.name,
          fitting_selection: state.selectedFittingType.name,
        });
      }
    }
  }, [state.selectedSlot, state.selectedLocation, state.selectedFittingType]);

  // Composite event: book_flow_loc_type_date_selected. Fires once per
  // session when all three of location, fitting type, and date are set.
  // ONCE_PER_SESSION dedup is enforced inside the dispatch module.
  useEffect(() => {
    if (state.selectedLocation && state.selectedFittingType && state.selectedDate) {
      emitAnalytics({
        event: 'book_flow_loc_type_date_selected',
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
      });
    }
  }, [state.selectedLocation, state.selectedFittingType, state.selectedDate]);

  // Marketing opt-in — fires once when promoOptIn transitions to true.
  useEffect(() => {
    if (state.promoOptIn && !prevPromoOptIn.current) {
      prevPromoOptIn.current = true;
      emitAnalytics({
        event: 'marketing_opt_in',
        opt_in_channels: ['email'],
        source_step: 'contact_info',
      });
    }
  }, [state.promoOptIn]);

  // Confirm appointment screen entry — fires book_flow_confirm_booking once.
  useEffect(() => {
    if (mode === 'confirm-appointment' && !confirmScreenFired.current) {
      if (
        !state.selectedLocation ||
        !state.selectedFittingType ||
        !state.selectedDate ||
        !state.selectedSlot
      ) {
        return;
      }
      confirmScreenFired.current = true;
      const slotStart = new Date(state.selectedSlot.start);
      const hh = String(slotStart.getHours()).padStart(2, '0');
      const mm = String(slotStart.getMinutes()).padStart(2, '0');
      const time_selected = `${hh}:${mm}`;
      emitAnalytics({
        event: 'book_flow_confirm_booking',
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
        time_selected,
        promo_code: state.promoCode.trim() || undefined,
        changed_location:
          confirmEnteredFromLocation.current !== null &&
          confirmEnteredFromLocation.current !== state.selectedLocation.id,
        changed_fitting_type:
          confirmEnteredFromFittingId.current !== null &&
          confirmEnteredFromFittingId.current !== state.selectedFittingType.id,
        value: parsePrice(state.selectedFittingType.pricingLabel),
        currency: 'USD',
      });
    }
  }, [
    mode,
    state.selectedLocation,
    state.selectedFittingType,
    state.selectedDate,
    state.selectedSlot,
    state.promoCode,
  ]);

  // Global click handler — fires book_flow_click for any element with
  // a data-cc-track attribute. Donald's GTM triggers can also bind to
  // these attributes directly; this gives us GA4 visibility regardless.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tracked = target.closest('[data-cc-track]') as HTMLElement | null;
      if (!tracked) return;
      const contentType = tracked.getAttribute('data-cc-track') ?? 'unknown';
      const contentId = tracked.getAttribute('data-cc-track-id') ?? undefined;
      emitAnalytics({
        event: 'book_flow_click',
        content_type: contentType,
        content_id: contentId,
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Post the booking to the middleware → SF Schedule. Called from the Confirm
  // Appointment screen's Confirm button.
  const handleConfirmBooking = async () => {
    if (
      !state.selectedLocation ||
      !state.selectedFittingType ||
      !state.selectedDate ||
      !state.selectedSlot
    )
      return;

    dispatch({ type: 'SUBMIT_START' });

    const { firstName, lastName } = splitName(state.fullName);
    const notesParts: string[] = [];
    notesParts.push(`Fitting type: ${state.selectedFittingType.name}`);
    if (state.promoCode.trim()) notesParts.push(`Promo code: ${state.promoCode.trim()}`);
    if (state.promoOptIn) notesParts.push('Customer opted into marketing promos');

    const payload: BookingPayload = {
      resourceId: state.selectedSlot.fitterId,
      start: state.selectedSlot.start,
      end: state.selectedSlot.end,
      fittingType: state.selectedFittingType.name,
      customer: { firstName, lastName, email: state.email.trim() },
      notes: notesParts.join(' · '),
    };

    try {
      const booking = await createBooking(payload);
      dispatch({ type: 'SUBMIT_SUCCESS', payload: booking });
      emit('cc:booking-widget:booking-confirmed', {
        confirmationNumber: booking.id,
        bookingId: booking.id,
        fittingType: state.selectedFittingType.name,
        location: state.selectedLocation.name,
      });

      // Set the persistent user_id so all subsequent events (including
      // the channelmix_conversion duplicate) carry it.
      if (booking.accountId) setAnalyticsUserId(booking.accountId);

      const slotStart = new Date(state.selectedSlot.start);
      const hh = String(slotStart.getHours()).padStart(2, '0');
      const mm = String(slotStart.getMinutes()).padStart(2, '0');
      const time_selected = `${hh}:${mm}`;
      const price = parsePrice(state.selectedFittingType.pricingLabel);

      const completePayload = {
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
        time_selected,
        promo_code: state.promoCode.trim() || undefined,
        changed_location:
          confirmEnteredFromLocation.current !== null &&
          confirmEnteredFromLocation.current !== state.selectedLocation.id,
        changed_fitting_type:
          confirmEnteredFromFittingId.current !== null &&
          confirmEnteredFromFittingId.current !== state.selectedFittingType.id,
        confirmation_number: booking.id,
        a_number: booking.id,
        waitlist: 'scheduled' as const,
        value: price,
        currency: 'USD',
      };

      // Primary conversion event — book_flow_complete.
      emitAnalytics({
        event: 'book_flow_complete',
        ...completePayload,
        store: state.selectedLocation.name,
        items: [
          {
            item_id: state.selectedFittingType.id,
            item_name: state.selectedFittingType.name,
            item_category: 'fitting',
            price,
            quantity: 1,
          },
        ],
      });

      // Intentional parallel event for ChannelMix attribution pipeline.
      emitAnalytics({
        event: 'channelmix_conversion',
        ...completePayload,
      });

      setMode('congrats');
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Booking failed (${err.status}). ${err.message}${err.body ? ' — ' + err.body.slice(0, 200) : ''}`
          : err instanceof Error
            ? err.message
            : 'Booking failed for an unknown reason.';
      dispatch({ type: 'SUBMIT_ERROR', payload: msg });
      emitAnalytics({ event: 'booking_error', error_message: msg });
    }
  };

  // Post a waitlist request to the middleware when the user dead-ends on
  // "no times available". Same shape as a booking conversion downstream —
  // lands on the congrats screen with a WL- confirmation number instead of
  // an A- number, and emits book_flow_complete with waitlist='WL' so the
  // legacy funnel + ChannelMix attribution capture it.
  const handleJoinWaitlist = async () => {
    if (
      !state.selectedLocation ||
      !state.selectedFittingType ||
      !state.selectedDate
    )
      return;

    dispatch({ type: 'WAITLIST_SUBMIT_START' });

    const { firstName, lastName } = splitName(state.fullName);
    const notesParts: string[] = [];
    notesParts.push(`Fitting type: ${state.selectedFittingType.name}`);
    notesParts.push(`Preferred date: ${state.selectedDate}`);
    if (state.promoCode.trim()) notesParts.push(`Promo code: ${state.promoCode.trim()}`);
    if (state.promoOptIn) notesParts.push('Customer opted into marketing promos');

    const payload = {
      locationId: state.selectedLocation.id,
      fittingTypeId: state.selectedFittingType.id,
      fittingTypeName: state.selectedFittingType.name,
      durationMin: state.selectedFittingType.durationMinutes,
      preferredDate: state.selectedDate,
      customer: { firstName, lastName, email: state.email.trim() },
      notes: notesParts.join(' · '),
    };

    try {
      const waitlist = await createWaitlist(payload);
      dispatch({ type: 'WAITLIST_SUBMIT_SUCCESS', payload: waitlist });
      emit('cc:booking-widget:booking-confirmed', {
        confirmationNumber: waitlist.id,
        bookingId: waitlist.id,
        fittingType: state.selectedFittingType.name,
        location: state.selectedLocation.name,
      });

      if (waitlist.accountId) setAnalyticsUserId(waitlist.accountId);

      const price = parsePrice(state.selectedFittingType.pricingLabel);
      const completePayload = {
        location: state.selectedLocation.name,
        fitting_selection: state.selectedFittingType.name,
        date_selected: state.selectedDate,
        // No time selected — waitlist doesn't pin a slot. Send empty string
        // rather than omitting so the legacy date_and_time reconstruction
        // still produces something usable.
        time_selected: '',
        promo_code: state.promoCode.trim() || undefined,
        changed_location:
          confirmEnteredFromLocation.current !== null &&
          confirmEnteredFromLocation.current !== state.selectedLocation.id,
        changed_fitting_type:
          confirmEnteredFromFittingId.current !== null &&
          confirmEnteredFromFittingId.current !== state.selectedFittingType.id,
        confirmation_number: waitlist.id,
        a_number: waitlist.id,
        waitlist: 'WL' as const,
        value: price,
        currency: 'USD',
      };

      emitAnalytics({
        event: 'book_flow_complete',
        ...completePayload,
        store: state.selectedLocation.name,
        items: [
          {
            item_id: state.selectedFittingType.id,
            item_name: state.selectedFittingType.name,
            item_category: 'fitting',
            price,
            quantity: 1,
          },
        ],
      });

      emitAnalytics({
        event: 'channelmix_conversion',
        ...completePayload,
      });

      setMode('congrats');
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Couldn’t join the waitlist (${err.status}). ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Couldn’t join the waitlist right now.';
      dispatch({ type: 'WAITLIST_SUBMIT_ERROR', payload: msg });
      emitAnalytics({ event: 'booking_error', error_message: msg });
    }
  };

  const handleResetWidget = () => {
    setMode(null);
    dispatch({ type: 'RESET' });
    setLeadCaptureDone(false);
    setHoldExpired(false);
  }

  /**
   * Modal close: in mid-flow modes (booking, confirm-appointment) we
   * preserve state so re-opening resumes where the user left off — closing
   * by accident shouldn't destroy a half-completed selection. In post-write
   * modes (congrats, set-up) closing means "I'm done", so we full-reset.
   */
  const handleCloseModal = () => {
    if (mode === 'congrats' || mode === 'set-up') {
      handleResetWidget();
    } else {
      setMode(null);
    }
  }

  const handleHoldExpired = () => {
    dispatch({ type: 'RESET_PRESERVE_LEAD' });
    setLeadCaptureDone(false);
    setHoldExpired(true);
    setMode('booking');
  }

  // After Get Set Up submission, transition to the final set-up screen.
  useEffect(() => {
    if (state.postBookingComplete && mode === 'congrats') {
      setMode('set-up');
      emitAnalytics({
        event: 'post_booking_completed',
        booking_id: state.confirmation?.id ?? 'unknown',
        ball_fitting_interest: state.postBookingBallFitting,
        sms_optin: state.postBookingSmsOptIn,
      });
    }
  }, [state.postBookingComplete, mode, state.confirmation, state.postBookingBallFitting, state.postBookingSmsOptIn]);

  return (
    <Modal isOpen={mode !== null} onClose={handleCloseModal}>
      <div ref={setRoot} className={styles.root} data-cc-layout={isDesktop ? 'desktop' : 'mobile'}>
        {mode === 'booking' && !leadCaptureDone && (
          <LeadCapture
            state={state}
            dispatch={dispatch}
            showExpiredNotice={holdExpired}
            onContinue={() => {
              setHoldExpired(false);
              setLeadCaptureDone(true);
              emit('cc:booking-widget:step-changed', { step: 'lead-capture' });
              // Fire BOTH events at lead capture per Donald's spec.
              emitAnalytics({
                event: 'book_flow_contact_info_complete',
                account_status: 'created', // refined by middleware response on book_flow_complete
                form_id: 'cc-booking-widget',
                form_name: 'CC Booking Widget',
              });
              emitAnalytics({
                event: 'generate_lead',
                value: 0,
                currency: 'USD',
                lead_source: window.location.pathname || 'unknown',
              });
            }}
          />
        )}

        {mode === 'booking' && leadCaptureDone && (
          <BookingStripPanel
            state={state}
            dispatch={dispatch}
            onJoinWaitlist={handleJoinWaitlist}
            onBook={() => {
              // Snapshot the values entering confirm so we can detect
              // "user changed something on the review page" later.
              confirmEnteredFromLocation.current = state.selectedLocation?.id ?? null;
              confirmEnteredFromFittingId.current = state.selectedFittingType?.id ?? null;
              // Pre-confirmation snapshot per Donald's schema.
              if (
                state.selectedLocation &&
                state.selectedFittingType &&
                state.selectedDate &&
                state.selectedSlot
              ) {
                const slotStart = new Date(state.selectedSlot.start);
                const hh = String(slotStart.getHours()).padStart(2, '0');
                const mm = String(slotStart.getMinutes()).padStart(2, '0');
                emitAnalytics({
                  event: 'book_flow_submit',
                  location: state.selectedLocation.name,
                  fitting_selection: state.selectedFittingType.name,
                  date_selected: state.selectedDate,
                  time_selected: `${hh}:${mm}`,
                  value: parsePrice(state.selectedFittingType.pricingLabel),
                  currency: 'USD',
                });
              }
              dispatch({ type: 'CLEAR_SUBMIT_ERROR' });
              setMode('confirm-appointment');
            }}
            layoutType={isDesktop ? 'desktop' : 'mobile'}
          />
        )}

        {mode === 'confirm-appointment' && (
          <ConfirmAppointment
            state={state}
            onConfirm={handleConfirmBooking}
            onEditField={(field: BookingEditField) => {
              for (const action of bookingEditFromConfirmActions(field)) {
                dispatch(action);
              }
              setMode('booking');
              // Bring the booking strip back into view. The Change buttons sit
              // mid/lower on the confirm screen; without an explicit scroll
              // the user's viewport stays where it was and the now-empty
              // field at the top of the strip is off-screen, making the
              // screen change feel like nothing happened.
              //
              // Use requestAnimationFrame so the new render has committed and
              // the rootRef position is accurate, then window.scrollTo using
              // the root's absolute Y in the document (scrollIntoView is a
              // no-op when the element is already "in view" by any amount —
              // which happens when the widget spans the viewport height).
              requestAnimationFrame(() => {
                const el = rootRef.current;
                if (!el) return;
                const targetY = window.scrollY + el.getBoundingClientRect().top - 20;
                window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
              });
            }}
            onHoldExpired={handleHoldExpired}
          />
        )}

        {mode === 'congrats' && (
          <BookingConfirmed state={state}>
            <GetSetUp state={state} dispatch={dispatch} />
          </BookingConfirmed>
        )}

        {mode === 'set-up' && <PostBookingComplete state={state} dispatch={dispatch} />}

        {/* Modal exposes its own close affordance (top-right ×) so the
            in-flow close button is redundant. Removed to avoid duplicate
            close UI inside the modal. */}
      </div>
      <TrafficDrawer />
    </Modal>
  );
}



function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === '') return { firstName: 'Guest', lastName: '(none)' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '(none)' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}
